
/* eslint-disable vue/no-mutating-props */
// `schedule` is state.schedule from the orchestrator, mutated in place.
import Calendar from '../v2/parts/Calendar.vue'
import { COPY } from './copy'

// The complete US set (advisor feedback: mockup's five was too narrow, the
// legacy form's full IANA list too noisy). ZIP auto-set still appends any
// zone outside this list dynamically.
const BASE_TZS = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'America/Puerto_Rico',
]

export default {
    components: { Calendar },
    props: {
        schedule: { type: Object, required: true },
        agentIds: { type: Array, default: () => [] },
        eligibleAgents: { type: Array, default: () => [] },
        pinnedAgent: { type: Object, default: null },
        // #727/#730: a client the open-state rule governs -- no video, and only
        // the allow-listed advisors (whose first names come in here).
        openStateRule: { type: Boolean, default: false },
        openStateAdvisorNames: { type: Array, default: () => [] },
        openStates: { type: Array, default: () => [] },
        // Paying-source restriction: which consult methods this client may book.
        // null → unrestricted (legacy behavior; every method offered).
        allowedMethods: { type: Array, default: null },
        sessionId: { type: String, required: true },
        // Payer / non-payer routing: the client's tier, for /dates/ and /times/.
        payer: { type: Boolean, default: null },
    },
    data () {
        return {
            COPY,
            // #679 K-rail: the due date fetched lazily from
            // getAepRecommendationDue once the K-rail first renders. null
            // until it resolves, and stays null (line omitted) on failure.
            aepDueDate: null,
            dueDateRequested: false,
        }
    },
    computed: {
        // Video is struck for AEP reviews (#679) and under the open-state rule (#727/#730).
        videoStruck () {
            return !!this.schedule.aep || this.openStateRule
        },
        advisorNames () {
            return this.openStateAdvisorNames.join(' or ')
        },
        callMethodsAvailable () {
            return this.allows('phone') || this.allows('video')
        },
        // Only shape allowedMethods can resolve to that has no call option at
        // all under aep (mockup panel K) — the backend/newClientAllowedMethods
        // branch always keeps 'survey' once methods are non-empty, so this is
        // never true for a non-aep client.
        surveyOnlyMode () {
            return !!this.schedule.aep && Array.isArray(this.allowedMethods) && this.allowedMethods.length === 1 && this.allowedMethods[0] === 'survey'
        },
        // Duration note (panel I), keyed by the consultation-type pill
        // (ClientStatusCard's schedule.reason). plan_review is the only
        // admin-mockup-verbatim string — #679 review fold-in minor (ii)
        // dropped the invented new_to_medicare/unanswered default, so this
        // (and the note that reads it, gated on it being truthy) renders
        // nothing until reason is answered as plan_review.
        durationText () {
            return this.schedule.reason === 'plan_review' ? COPY.rail.durationPlanReview : ''
        },
        formattedDueDate () {
            return this.aepDueDate ? this.$moment(this.aepDueDate).format('MM/DD/YYYY') : ''
        },
        tzOptions () {
            // ZIP auto-set can land outside the mockup's five (e.g. Phoenix) —
            // append it so the select always shows the active zone.
            return BASE_TZS.includes(this.schedule.timezone)
                ? BASE_TZS
                : BASE_TZS.concat([this.schedule.timezone])
        },
        calValue () {
            const { date, time, agent, agents } = this.schedule
            return { date, time, agent, agents }
        },
    },
    watch: {
        surveyOnlyMode: {
            immediate: true,
            handler (val) {
                if (val) {
                    this.loadDueDate()
                } else {
                    // #679 review fold-in minor (iii): reset so a later
                    // re-entry (aep toggled back on, or allowedMethods
                    // re-resolving to survey-only again) refetches rather
                    // than silently reusing a stale requested-once flag.
                    this.dueDateRequested = false
                    this.aepDueDate = null
                }
            },
        },
    },
    methods: {
        allows (m) {
            return !this.allowedMethods || this.allowedMethods.includes(m)
        },
        async loadDueDate () {
            if (this.dueDateRequested) return
            this.dueDateRequested = true
            try {
                const resp = await this.$api.getAepRecommendationDue()
                this.aepDueDate = (resp && resp.due_date) || null
            } catch (e) {
                this.aepDueDate = null
            }
        },
        tzText (tz) {
            const offset = this.$moment.tz ? this.$moment.tz(tz).format('Z') : ''
            return offset ? `${tz} (GMT${offset})` : tz
        },
        clearSlot () {
            // Emit BEFORE nulling: the orchestrator's releaseHold reads
            // schedule.time/agent synchronously to build the release request.
            this.$emit('release-hold')
            this.$set(this.schedule, 'date', null)
            this.$set(this.schedule, 'time', null)
            this.$set(this.schedule, 'agent', null)
            this.$set(this.schedule, 'agents', null)
        },
        setMethod (m) {
            // Struck-through + disabled under aep (mockup panel I): the click
            // handler is a no-op rather than hiding the pill, so an assistant
            // sees why video is unavailable instead of assuming it's broken.
            if (m === 'video' && this.videoStruck) return
            if (this.schedule.method === m) return
            this.$set(this.schedule, 'method', m)
            this.clearSlot()
        },
        setTimezone (tz) {
            this.$set(this.schedule, 'timezone', tz)
        },
        setAdvisor (v) {
            const id = v === null || v === '' || v === 'null' ? null : parseInt(v)
            if (this.schedule.force_agent === id) return
            this.$set(this.schedule, 'force_agent', id)
            this.$emit('advisor-change')
            this.clearSlot()
        },
        onCalInput (val) {
            if (this.schedule.time && !val.time) this.$emit('release-hold')
            this.$set(this.schedule, 'date', val.date)
            this.$set(this.schedule, 'time', val.time)
            this.$set(this.schedule, 'agent', val.agent)
            this.$set(this.schedule, 'agents', val.agents)
        },
        reloadTimes () {
            if (this.$refs.calendar) this.$refs.calendar.loadTimes()
        },
    },
}
